'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface VideoOverlay {
  time: string;
  title: string;
  description: string;
}

interface VideoDemoData {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  alt: string;
  duration: string;
  overlays: VideoOverlay[];
}

interface VideoDemoProps {
  video: VideoDemoData;
}

const VideoDemo = ({ video }: VideoDemoProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOverlays, setShowOverlays] = useState(true);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Video Container */}
      <div className="relative aspect-video bg-primary group">
        <AppImage
          src={video.thumbnail}
          alt={video.alt}
          className="w-full h-full object-cover"
        />
        
        {/* Play Overlay */}
        {!isPlaying && (
          <div className="absolute inset-0 bg-primary/60 backdrop-blur-sm flex items-center justify-center">
            <button
              onClick={() => setIsPlaying(true)}
              className="w-20 h-20 bg-accent rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-aerospace"
            >
              <Icon name="PlayIcon" size={32} className="text-primary ml-1" />
            </button>
          </div>
        )}

        {/* Duration Badge */}
        <div className="absolute top-4 right-4 px-3 py-1 bg-primary/80 backdrop-blur-sm rounded-full border border-border">
          <span className="font-cta text-xs text-foreground">{video.duration}</span>
        </div>

        {/* Technical Overlays */}
        {isPlaying && showOverlays && (
          <div className="absolute bottom-4 left-4 right-4 space-y-2 animate-fade-in">
            {video.overlays.map((overlay, index) => (
              <div
                key={index}
                className="bg-card/90 backdrop-blur-md rounded-lg p-3 border border-accent/30"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-cta text-xs text-accent">{overlay.time}</span>
                      <span className="font-headline text-sm font-semibold text-foreground">
                        {overlay.title}
                      </span>
                    </div>
                    <p className="font-body text-xs text-technical-gray">
                      {overlay.description}
                    </p>
                  </div>
                  <Icon name="InformationCircleIcon" size={20} className="text-accent ml-2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Controls */}
        {isPlaying && (
          <div className="absolute bottom-4 right-4 flex space-x-2">
            <button
              onClick={() => setShowOverlays(!showOverlays)}
              className="p-2 bg-card/80 backdrop-blur-sm rounded-lg border border-border hover:border-accent transition-colors duration-300"
            >
              <Icon
                name={showOverlays ? 'EyeIcon' : 'EyeSlashIcon'}
                size={20}
                className="text-foreground"
              />
            </button>
          </div>
        )}
      </div>

      {/* Video Info */}
      <div className="p-4">
        <h3 className="font-headline text-lg font-bold text-foreground mb-2">
          {video.title}
        </h3>
        <p className="font-body text-sm text-technical-gray">
          {video.description}
        </p>
      </div>
    </div>
  );
};

export default VideoDemo;